import {Component, ChangeDetectorRef, HostBinding, OnDestroy} from '@angular/core';
import {ScreenService, AppInfoService} from './shared/services';
import {EventService} from './services/event.service';
import {LoadingModel} from './models/shared/loading.model';
import {AppPageEnum, EventName, SocketEventType} from './shared/enums';
import {SettingsService} from './services/settings.service';
import {UserService} from './services';
import {ConfirmModel} from './models/shared/confirm.model';
import {AttentionModel} from './models/shared/attention.model';
import {SocketSubscriptionRequest} from './models/shared/socket-subscription.model';
import {AppNotify} from './utilities';
import {Router} from '@angular/router';
import {SocketService} from './services/socket.service';
import {Subscription} from 'rxjs';
import {AppStorage} from './utilities/storage';
import {ACCESS_TOKEN_KEY} from './shared/constants';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnDestroy {
    @HostBinding('class') hostClass = 'app';

    private updateHostClass(): void {
        const sizeClassName = Object.keys(this.screen.sizes).filter(cl => this.screen.sizes[cl]).join(' ');
        this.hostClass = `${sizeClassName} app`;
    }

    loadingData: LoadingModel;
    confirmData: ConfirmModel;
    attentionData: AttentionModel;

    showConfirmResponseScope = AppPageEnum.Base;
    showConfirmAttentionScope = AppPageEnum.Base;

    /** True when logged in via admin impersonation (bridge code), not Kinde */
    get isImpersonating(): boolean {
      return !!AppStorage.getTokenData(ACCESS_TOKEN_KEY) && !!localStorage.getItem('sz_impersonating');
    }

    returnToAdmin(): void {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem('ert');
      localStorage.removeItem('usif');
      localStorage.removeItem('sz_impersonating');
      window.location.href = 'https://statementzen.com/admin';
    }

    private statementExtractionSubscription?: Subscription;
    private erpConnectionStatusSubscription?: Subscription;
    private erpExtractionProgressSubscription?: Subscription;

    // Track active socket subscriptions to avoid duplicates
    private activeSocketSubscriptions = new Set<string>();

    constructor(private screen: ScreenService, public appInfo: AppInfoService
        , private eventService: EventService
        , private userService: UserService
        , private settingService: SettingsService
        , private router: Router
        , private socketService: SocketService
        , private cdr: ChangeDetectorRef) {
        this.loadingData = new LoadingModel();
        this.confirmData = new ConfirmModel();
        this.attentionData = new AttentionModel();

        this.updateHostClass();
        this.screen.changed.subscribe(() => {
            this.updateHostClass();
            this.cdr.detectChanges();
        });

        if (userService.isLoggedIn()) {
            this.eventService.on(EventName.ShowLoading,
                (data) => {
                    if (!data) {
                        this.loadingData = new LoadingModel();
                    } else {
                        this.loadingData = data;
                    }
                    this.cdr.detectChanges();
                });

            this.eventService.on(EventName.ShowAttention, (data) => {
                if (!data) {
                    this.attentionData = new AttentionModel();
                } else {
                    this.showConfirmAttentionScope = data.confirmAttentionScope;
                    this.attentionData = data;
                }
                this.cdr.detectChanges();
            });

            this.eventService.on(EventName.ShowConfirm, (data) => {
                if (!data) {
                    this.confirmData = new ConfirmModel();
                } else {
                    this.showConfirmResponseScope = data.confirmResponseScope;
                    this.confirmData = data;
                }
                this.cdr.detectChanges();
            });

            this.eventService.on(EventName.ErpInterrupted, (data) => {
                if (!data) {
                    this.attentionData = new AttentionModel();
                } else {
                    this.showConfirmResponseScope = data.confirmResponseScope;
                    this.attentionData = data;
                }
                this.cdr.detectChanges();
            });

            this.settingService.getTooltips()
                .subscribe(result => {
                    if (result && result.data) {
                        this.userService.setTooltips(result.data);
                    }
                });

            // Listen for socket subscription requests (lazy loading)
            this.eventService.on(EventName.SubscribeToSocketEvents, (request: SocketSubscriptionRequest) => {
                this.handleSocketSubscription(request);
            });
        }
    }

    /**
     * Handle lazy socket subscription requests from components
     * Only subscribe to socket events when a component actually needs them
     */
    private handleSocketSubscription(request: SocketSubscriptionRequest): void {
        const subscriptionKey = this.getSubscriptionKey(request);

        // Check if already subscribed
        if (this.activeSocketSubscriptions.has(subscriptionKey)) {
            // console.log('[AppComponent] Already subscribed to:', subscriptionKey);
            return;
        }

        // console.log('[AppComponent] Subscribing to socket events:', request);

        // Connect to socket service if not connected
        this.socketService.connect();

        switch (request.type) {
            case SocketEventType.StatementExtraction:
                // skipcq: JS-0339
                this.subscribeToStatementExtraction(request.statementId!);
                break;
            case SocketEventType.ErpConnection:
            case SocketEventType.ApTransactionExtraction:
                // skipcq: JS-0339
                this.subscribeToErpExtraction(request.companyId!, request.erpType!);
                break;
            default:
                // no default
                break;
        }

        // Mark as subscribed
        this.activeSocketSubscriptions.add(subscriptionKey);
    }

    /**
     * Subscribe to statement extraction socket events
     */
    private subscribeToStatementExtraction(statementId: string): void {
        // Subscribe to socket room
        this.socketService.subscribeToStatement(statementId);

        // Listen to socket events and broadcast to in-app subscribers (only once)
        if (!this.statementExtractionSubscription) {
            this.statementExtractionSubscription = this.socketService.onStatementExtractionStatus$
                .subscribe(data => {
                    // console.log('[AppComponent] Received statement extraction status:', data);

                    // Broadcast to in-app subscribers
                    this.eventService.broadcast(EventName.StatementExtractionStatus, data);

                    // Note: Do not unsubscribe from the statement room here, as multiple statements
                    // may be extracting simultaneously. The room subscription should remain active
                    // to receive events for all statements. Individual statement status
                    // is handled by the components themselves.
                });
        }
    }

    /**
     * Subscribe to ERP extraction socket events
     */
    private subscribeToErpExtraction(companyId: string, erpType: string): void {
        // Subscribe to socket room
        this.socketService.subscribeToErp(companyId, erpType);

        // Listen to ERP connection status (only once)
        if (!this.erpConnectionStatusSubscription) {
            this.erpConnectionStatusSubscription = this.socketService.onErpConnectionStatus$
                .subscribe(data => {
                    // console.log('[AppComponent] Received ERP connection status:', data);

                    // Broadcast to in-app subscribers
                    this.eventService.broadcast(EventName.ErpConnectionStatus, data);

                    // Note: Do not unsubscribe from the ERP room here, as multiple organizations
                    // may be extracting simultaneously. The room subscription should remain active
                    // to receive events for all organizations. Individual organization status
                    // is handled by the components themselves.
                });
        }

        // Listen to ERP extraction progress (only once)
        if (!this.erpExtractionProgressSubscription) {
            this.erpExtractionProgressSubscription = this.socketService.onErpExtractionProgress$
                .subscribe(data => {
                    // console.log('[AppComponent] Received ERP extraction progress:', data);

                    // Broadcast to in-app subscribers
                    this.eventService.broadcast(EventName.ErpExtractionProgress, data);
                });
        }
    }

    /**
     * Generate unique key for tracking subscriptions
     */
    // skipcq: JS-0105
    private getSubscriptionKey(request: SocketSubscriptionRequest): string {
        switch (request.type) {
            case SocketEventType.StatementExtraction:
                return `${request.type}:${request.statementId}`;
            case SocketEventType.ErpConnection:
            case SocketEventType.ApTransactionExtraction:
                return `${request.type}:${request.companyId}:${request.erpType}`;
            default:
                return request.type;
        }
    }

    ngOnDestroy(): void {
        this.statementExtractionSubscription?.unsubscribe();
        this.erpConnectionStatusSubscription?.unsubscribe();
        this.erpExtractionProgressSubscription?.unsubscribe();
    }

    onLoadPanelHidden() {
        this.loadingData.visible = false;
        this.eventService.broadcast(EventName.ShowLoading, this.loadingData);
    }

    // skipcq: JS-0323
    handleConfirmed(sender: any) {
        this.confirmData.visible = false;
        this.confirmData.confirmed = sender;
        this.eventService.broadcast(EventName.ConfirmResponse, this.confirmData, this.showConfirmResponseScope);
    }

    // skipcq: JS-0323, JS-0356
    handleAttention(_sender: any) {
        if (this.attentionData.action === 'DEFAULT' && !location.pathname.includes('erp-integration')) {
            this.router.navigate(['/erp-integration']);
            return;
        }
        if (this.attentionData.action === 'SUBSCRIPTION_VALIDATION' && this.attentionData.buttonText === 'Go to Subscription') {
            location.href = 'https://statementzen.com/my-account/';
            return;
        }
        this.attentionData.visible = false;
        this.eventService.broadcast(EventName.ConfirmAttention, this.attentionData, this.showConfirmAttentionScope);
    }

    handleAttentionDismissed() {
        localStorage.setItem('erp_interrupt_dismissed', Date.now().toString());
        this.attentionData.visible = false;
    }

    handleAttentionHidden() {
        if (this.attentionData.action === 'SUBSCRIPTION_VALIDATION' && this.attentionData.buttonText === 'Go to Subscription') {
            this.router.navigate(['/statements']);
            return;
        }
    }

    triggerFileUpload() {
        const fileUpload = document.getElementById('file-upload') as HTMLInputElement;
        fileUpload.click();
    }

    // skipcq: JS-0323
    handleFileChange(event: any) {
        const files = Array.from<File>(event.target.files);
        if (this.confirmData.files?.length + files.length > 5) {
            // clean input
            event.target.value = '';
            AppNotify.error('You can only upload up to 5 files');
            return;
        }

        if (!this.confirmData.files) {
            this.confirmData.files = [];
        }

        this.confirmData.files.push(...files);
        // clean input
        event.target.value = '';
    }
}
